import styled from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { Button } from '@savgroup-front-common/core/atoms/button';
import { getCustomComponentProperty } from '@savgroup-front-common/core/helpers';
import { fontStyle } from '@savgroup-front-common/core/theme/helpers/fontStyle';
import { transparentize } from '@savgroup-front-common/primitive/helpers/cssColor';
import { fontWeight } from '@savgroup-front-common/primitive/helpers/fontWeight';
import { spacing } from '@savgroup-front-common/primitive/helpers/spacing';
import {
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

export const $HomeContentWrapper = styled.div`
  background-color: white;
  border-radius: ${({ theme }) =>
    getCustomComponentProperty({
      theme,
      componentName: SUPPORTED_COMPONENTS.CARD,
      property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
      fallback: theme.borders.radius,
    })};
  min-height: 70vh;
  padding: ${spacing('xl')} ${spacing(12)};
  gap: ${spacing('xl')};
  display: flex;
  flex-direction: column;

  @media ${media.maxWidth.xs} {
    padding: ${spacing(3.5)};
  }

  & [class*='TabContainer'] {
    padding: 0;
  }

  & [class*='RouteTabLink'] {
    padding: ${spacing(3.5)};
    padding-top: 0;
  }

  & [class*='TabViewList'] {
    width: fit-content;
    padding: 0;

    & a:-webkit-any-link {
      text-decoration: none;
    }

    & [class*='RouteTabLink'] {
      & [class*='MessageWithIcon'] {
        flex-direction: column;
        gap: ${spacing('xs')};

        & > svg {
          width: ${spacing('xxl')};
          height: ${spacing('xxl')};
          margin: 0;
          fill: none;
        }
      }
      &:hover,
      &:focus {
        & [class*='MessageWithIcon'] > svg path[stroke] {
          stroke: ${({ theme }) => theme.colors.primary} !important;
        }
        & [class*='MessageWithIcon'] > svg path[fill] {
          fill: ${({ theme }) => theme.colors.primary} !important;
        }
      }
      &:focus {
        & [class*='MessageWithIcon'] > span {
          color: ${({ theme }) => theme.colors.primary};
        }
      }
    }
  }

  & [class*='TabViewNav'] {
    width: fit-content;
    padding: 0;
    margin: auto;
  }
`;

export const $AddProductWrapper = styled.div`
  padding: ${spacing(7)};
`;

export const $CloseButton = styled(Button)`
  padding: 0;
  height: auto;
  width: auto;
  min-height: auto;
  min-width: auto;
  margin-bottom: ${spacing(3.5)};
`;

export const $HelperContainerTitle = styled('div')`
  margin: ${spacing(7)} 0 ${spacing(3.5)};
  ${fontStyle('xl')};
  font-weight: ${fontWeight('semibold')};
`;

export const $HomePageAddProductContent = styled.div`
  display: flex;
  width: 100%;
  flex-wrap: wrap;
  gap: ${spacing(3.5)};
  margin-top: ${spacing(7)};
  overflow: auto;
  max-height: calc(100vh - 225px);
  padding-left: ${spacing('sm')};
  padding-top: ${spacing(3.5)};
  padding-bottom: ${spacing(11)};
`;

export const $HomePageAddProductAction = styled.div<{ $isVisible?: boolean }>`
  position: absolute;
  bottom: ${({ $isVisible }) => ($isVisible ? spacing(3.5) : '0')};
  left: ${spacing(7)};
  right: ${spacing(7)};
  visibility: ${({ $isVisible }) => ($isVisible ? 'visible' : 'hidden')};
  transition: bottom 0.2s ease-in-out;
  z-index: 1;

  && button {
    border-radius: ${({ theme }) => theme.tw.radius.sm};
  }
`;

export const $ProductItem = styled.div<{ $isChecked?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${spacing(3.5)};
  border-radius: ${({ theme }) => theme.tw.radius.sm};
  border: 1px solid
    ${({ theme, $isChecked }) =>
      $isChecked ? theme.colors.primary : 'transparent'};
  box-shadow: ${({ theme, $isChecked }) =>
    $isChecked
      ? `0 0 ${spacing('sm')} ${transparentize(0.7, theme.colors.primary)}`
      : `0 0 ${spacing(3.5)} rgba(0, 0, 0, 0.05)`};
  max-width: calc(33% - ${spacing('md')});
  flex: 1;
  flex-basis: 33.33%;
  position: relative;

  @media ${media.maxWidth.xs} {
    max-width: calc(50% - ${spacing('md')});
    flex-basis: 50%;
  }

  & [class*='Checkboxstyles__CheckboxContainer'] {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    padding: ${spacing(3.5)};
    margin: 0;
  }

  & input {
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: auto;
    height: auto;
  }

  & [class*='Checkboxstyles__StyledCheckbox'] {
    top: ${spacing(3.5)};
    left: ${spacing(3.5)};
    border-radius: 50%;
    width: ${spacing(7)};
    height: ${spacing(7)};
    background: ${({ theme }) => theme.systemColors.defaultBackground};
    border-width: 1px;
    padding: ${spacing(1.5)};
    background: ${({ $isChecked, theme }) =>
      $isChecked ? theme.colors.primary : theme.systemColors.defaultBackground};
  }

  &:hover {
    border: 1px solid ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 ${spacing('sm')}
      ${({ theme }) => transparentize(0.7, theme.colors.primary)};
  }
`;

export const $ProductItemPicture = styled.div`
  width: 100px;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${spacing(5)};
  overflow: hidden;

  & img {
    max-width: 100%;
  }
`;

export const $ProductItemTitle = styled.div`
  color: ${({ theme }) => theme.colors.darkBackground};
  ${fontStyle('lg')};
  font-weight: ${fontWeight('bold')};
  margin-bottom: ${spacing('xs')};
  align-self: baseline;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
`;

export const $ProductItemDescription = styled.div`
  color: ${({ theme }) => theme.colors.paragraphTextColor};
  ${fontStyle('sm')};
  font-weight: ${fontWeight('normal')};
  align-self: baseline;
  overflow: hidden;
  max-width: 100%;
  text-overflow: ellipsis;
`;

export const $HomeProductsListWrapper = styled.div`
  gap: ${spacing(3.5)};
  display: flex;
  flex-direction: column;

  & [class*='MonoProductInfostyles__Card'],
  &
    [class*='MonoProductInfostyles__Card']
    ~ [class*='MonoProductInfostyles__Card'] {
    margin-top: 0;
    margin-bottom: 0;
  }
`;
